// Scoring engine. Pure functions. Mirror of the table in
// lennon/management/linkedin-competition-q2-2026.md
//
// To change scoring mid-quarter: edit the SCORING object below, then call the
// recompute-points Netlify Function (or hit the "Recompute" button in /admin).
// Because point_events is append-only and recompute is idempotent, this is safe.

import { startOfWeek, isSameWeek } from 'date-fns'

export const QUARTER_START = new Date('2026-05-01T00:00:00Z')
export const QUARTER_END = new Date('2026-07-31T23:59:59Z')
export const RESULTS_DATE = new Date('2026-08-06T00:00:00Z')

export const SCORING = {
  ORIGINAL_POST: { points: 10, cap: null, label: 'Original post' },
  COMMENT: { points: 5, cap: { period: 'week', limit: 5 }, label: 'Thoughtful comment' },
  REPOST: { points: 3, cap: { period: 'week', limit: 3 }, label: 'Repost with commentary' },
  IMPRESSIONS_1K: { points: 10, cap: null, label: '1k impressions on a post', threshold: 1000 },
  IMPRESSIONS_5K: { points: 25, cap: null, label: '5k impressions on a post', threshold: 5000 },
  IMPRESSIONS_20K: { points: 100, cap: null, label: '20k impressions on a post', threshold: 20000 },
  QUALIFIED_INBOUND: { points: 150, cap: null, label: 'Qualified inbound' },
}

// Impression tiers ordered high → low so we can pick the highest tier a single
// post hits and award only that one. Re-derived from SCORING so it can never
// drift from the rule above.
const IMPRESSION_TIERS = [
  SCORING.IMPRESSIONS_20K,
  SCORING.IMPRESSIONS_5K,
  SCORING.IMPRESSIONS_1K,
]

export const ACTIVITY_TYPES = [
  { id: 'post', label: 'Post', scoringKey: 'ORIGINAL_POST', description: 'A new post you wrote on LinkedIn.' },
  { id: 'comment', label: 'Comment', scoringKey: 'COMMENT', description: 'A thoughtful comment on an ICP or partner post.' },
  { id: 'repost', label: 'Repost', scoringKey: 'REPOST', description: 'A repost of someone else\'s post with your own commentary.' },
  { id: 'inbound', label: 'Inbound', scoringKey: 'QUALIFIED_INBOUND', description: 'A qualified DM or inbound conversation that came from one of your posts.' },
]

export const CONTENT_GATE_CATEGORIES = [
  { id: 'customer_story', label: 'Customer story', description: 'Real problem, what we did, outcome. Names only with written permission.' },
  { id: 'technical_insight', label: 'Technical insight', description: 'An opinion backed by actual expertise.' },
  { id: 'behind_the_scenes', label: 'Behind the scenes', description: 'What we\'re building, what we got wrong. Builder voice, not PR.' },
  { id: 'industry_take', label: 'Industry take', description: 'A defensible opinion on where the market is going.' },
  { id: 'data', label: 'Data', description: 'A benchmark, stat or chart from our data or credible public sources. Cite it.' },
]

const TYPE_TO_SCORING = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.id, t.scoringKey]))

/**
 * Recompute all point events for a single participant's activities.
 * Idempotent. Returns an array of point_events rows ready to upsert.
 *
 * Inputs:
 *   participantId: uuid
 *   activities: list of activity rows (with optional joined post_stats)
 *
 * Each activity may produce 0..2 point events:
 *   - 1 base event for the activity itself (subject to caps and gate)
 *   - 1 impression bonus event for the highest tier that single post hits
 *     (1k / 5k / 20k). Highest tier only — a post that hits 20k earns one
 *     +100, not 10+25+100. Repeats per post, so a leader doesn't run out of
 *     bonus to chase.
 */
export function recomputePoints(participantId, activities) {
  if (!Array.isArray(activities)) return []

  const sorted = [...activities].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  )

  const events = []
  const weekUsage = new Map()

  for (const activity of sorted) {
    if (!activity.content_gate_passed) continue

    const scoringKey = TYPE_TO_SCORING[activity.type]
    if (!scoringKey) continue

    // Inbound only counts if explicitly approved.
    if (activity.type === 'inbound' && !activity.inbound_approved) continue

    const rule = SCORING[scoringKey]

    if (rule.cap) {
      const weekKey = isoWeekKey(activity.created_at)
      const used = weekUsage.get(weekKey + ':' + activity.type) || 0
      if (used >= rule.cap.limit) continue
      weekUsage.set(weekKey + ':' + activity.type, used + 1)
    }

    events.push({
      participant_id: participantId,
      activity_id: activity.id,
      points: rule.points,
      reason: rule.label,
      created_at: activity.created_at,
    })

    // Per-post impression bonus. post_stats is append-only and PostgREST does
    // not guarantee embedded-row ordering, so take the max impressions across
    // every snapshot (impressions only ever go up) and the latest fetched_at.
    const statsRows = activity.post_stats || (activity.latest_stats ? [activity.latest_stats] : [])
    const impressions = statsRows.reduce((m, s) => Math.max(m, s?.impressions || 0), 0)
    const latestFetchedAt = statsRows.reduce((latest, s) => {
      if (!s?.fetched_at) return latest
      return !latest || s.fetched_at > latest ? s.fetched_at : latest
    }, null)
    const tier = highestImpressionTier(impressions)
    if (tier) {
      events.push({
        participant_id: participantId,
        activity_id: activity.id,
        points: tier.points,
        reason: tier.label,
        created_at: latestFetchedAt || activity.created_at,
      })
    }
  }

  return events
}

// Return the top tier this single post qualifies for, or null. Highest-only:
// a 25k post returns the 20k tier, not all three.
function highestImpressionTier(impressions) {
  if (!impressions || impressions <= 0) return null
  for (const tier of IMPRESSION_TIERS) {
    if (impressions >= tier.threshold) return tier
  }
  return null
}

/**
 * Caps remaining for the current ISO week.
 * Returns { comments: { used, limit, remaining }, reposts: {...} }
 */
export function weeklyCapsRemaining(activities, now = new Date()) {
  const out = {
    comment: { used: 0, limit: SCORING.COMMENT.cap.limit, remaining: SCORING.COMMENT.cap.limit },
    repost: { used: 0, limit: SCORING.REPOST.cap.limit, remaining: SCORING.REPOST.cap.limit },
  }

  for (const a of activities) {
    if (!a.content_gate_passed) continue
    if (!isSameWeek(new Date(a.created_at), now, { weekStartsOn: 1 })) continue
    if (a.type === 'comment') out.comment.used++
    if (a.type === 'repost') out.repost.used++
  }
  out.comment.remaining = Math.max(0, out.comment.limit - out.comment.used)
  out.repost.remaining = Math.max(0, out.repost.limit - out.repost.used)
  return out
}

/**
 * Pre-submit point preview. What would this activity earn if logged now?
 * Returns { base, conditional: [strings], totalIfThresholdsHit }
 *
 * Impression bonuses are per-post, highest-tier only. The conditional list is
 * shown as a ladder so the user sees how each tier replaces the one below.
 */
export function previewPoints(type, projectedImpressions = 0) {
  const scoringKey = TYPE_TO_SCORING[type]
  if (!scoringKey) return { base: 0, conditional: [], totalIfThresholdsHit: 0 }
  const rule = SCORING[scoringKey]
  const conditional = []
  let total = rule.points
  if (type === 'post' || type === 'repost') {
    conditional.push(`+${SCORING.IMPRESSIONS_1K.points} if this post hits 1,000 impressions`)
    conditional.push(`+${SCORING.IMPRESSIONS_5K.points} if this post hits 5,000 impressions (replaces the 1k bonus)`)
    conditional.push(`+${SCORING.IMPRESSIONS_20K.points} if this post hits 20,000 impressions (replaces the 5k bonus)`)
    const tier = highestImpressionTier(projectedImpressions)
    if (tier) total += tier.points
  }
  return { base: rule.points, conditional, totalIfThresholdsHit: total }
}

function isoWeekKey(dateStr) {
  const d = new Date(dateStr)
  const monday = startOfWeek(d, { weekStartsOn: 1 })
  return monday.toISOString().slice(0, 10)
}

export function totalPoints(pointEvents) {
  return (pointEvents || []).reduce((sum, e) => sum + (e.points || 0), 0)
}

export function pointsByDay(pointEvents) {
  const map = new Map()
  for (const e of pointEvents || []) {
    const day = e.created_at.slice(0, 10)
    map.set(day, (map.get(day) || 0) + e.points)
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, points]) => ({ date, points }))
}
